/**
 * Overflow State Banner
 * Displays when user is in overflow state (storage limit exceeded)
 * Shows days remaining and restricts functionality
 * Uses white styling like SuspensionBanner
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Download, X, Clock, Mail, MessageSquare } from 'lucide-react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';
import { formatBytes } from '@utils/format';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@config/constants';

const SUPPORT_EMAIL = 'info@fyle-cloud.com';

interface OverflowStatus {
  state: 'none' | 'active' | 'grace_period' | 'expired';
  startedAt: string | null;
  gracePeriodDays: number;
  daysRemaining: number | null;
  storageUsed: number;
  storageLimit: number;
  isDowngradeWarning: boolean; // True if this is from downgrade_warning_started_at (3 days warning with all functions, then suspension, then 14 days until deletion), false if overflow_state (21 days grace period with restricted functions)
}

/**
 * Hook to manage banner dismissal state
 * Banner is always shown on page load, dismissal only persists for current session
 */
const useBannerDismissal = () => {
  // Always start with banner visible (not dismissed)
  const [isDismissed, setIsDismissedState] = useState(false);

  useEffect(() => {
    const handleReopen = () => {
      setIsDismissedState(false);
    };

    window.addEventListener('overflow-banner-reopen', handleReopen);
    return () => {
      window.removeEventListener('overflow-banner-reopen', handleReopen);
    };
  }, []);

  const setIsDismissed = (value: boolean) => {
    setIsDismissedState(value);
    // Don't persist to localStorage - dismissal only for current session
    // This ensures banner is always visible when page is opened
  };

  return { isDismissed, setIsDismissed };
};

export const OverflowBanner: React.FC = () => {
  const userId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();
  const [overflowStatus, setOverflowStatus] = useState<OverflowStatus | null>(null);
  const { isDismissed, setIsDismissed } = useBannerDismissal();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const checkOverflowState = async () => {
      try {
        // Get profile with overflow state and downgrade warning
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('overflow_state, overflow_started_at, overflow_grace_period_days, storage_used, storage_limit, downgrade_warning_started_at, is_suspended, plan')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('Error checking overflow state:', error);
          setIsLoading(false);
          return;
        }

        if (!profile) {
          setIsLoading(false);
          return;
        }

        // CRITICAL: Only show banner if storage is ACTUALLY exceeded
        const storageUsed = profile.storage_used || 0;
        const storageLimit = profile.storage_limit || 0;
        const isActuallyOverflow = storageUsed > storageLimit;

        if (!isActuallyOverflow) {
          setOverflowStatus(null);
          setIsLoading(false);
          return;
        }

        // If suspended, don't show overflow banner (suspension banner will show instead)
        if (profile.is_suspended) {
          setOverflowStatus(null);
          setIsLoading(false);
          return;
        }

        // Check if this is a downgrade warning (3 days warning period) or normal overflow
        // Downgrade: 3 days warning (all functions) → then suspension → then 14 days until deletion
        // Normal overflow: 21 days grace period (restricted functions)
        const hasDowngradeWarning = !!profile.downgrade_warning_started_at;
        const state = (profile.overflow_state || 'none') as OverflowStatus['state'];
        
        // DEBUG: Log current state for troubleshooting
        console.log('[OverflowBanner] Debug:', {
          userId,
          hasDowngradeWarning,
          downgrade_warning_started_at: profile.downgrade_warning_started_at,
          overflow_state: state,
          overflow_started_at: profile.overflow_started_at,
          storageUsed,
          storageLimit,
        });
        
        // CRITICAL: Downgrade warning has PRIORITY over normal overflow
        // If downgrade_warning_started_at is set, always show downgrade warning (3 days)
        // regardless of overflow_state or overflow_started_at
        
        // Only set overflow_state for normal overflow (not downgrade warning)
        // IMPORTANT: If downgrade_warning_started_at exists, DO NOT set overflow_state
        // CRITICAL: Also check if user just downgraded (plan changed recently)
        // If downgrade happened recently, wait a bit before setting overflow_state
        // to allow downgrade_warning_started_at to be set first
        
        // Check if this might be a downgrade scenario (plan was recently changed to free or lower tier)
        // We'll be conservative and NOT set overflow_state if plan is 'free' and storage is exceeded
        // This gives the downgrade check in UserManagementModal time to set downgrade_warning_started_at
        const mightBeDowngrade = profile.plan === 'free' && storageUsed > storageLimit;
        
        if (state === 'none' && !hasDowngradeWarning && !mightBeDowngrade) {
          // Set overflow_state if not already set (trigger might not have fired)
          // This is for normal overflow cases (not downgrade)
          // BUT: Don't set if this might be a downgrade scenario
          try {
            await supabase
              .from('profiles')
              .update({
                overflow_state: 'active',
                overflow_started_at: profile.overflow_started_at || new Date().toISOString(),
                overflow_grace_period_days: profile.overflow_grace_period_days || 21,
              })
              .eq('id', userId);
          } catch (error) {
            console.warn('Failed to set overflow_state:', error);
            // Continue anyway - we'll still show the banner
          }
        } else if (hasDowngradeWarning && state !== 'none') {
          // If downgrade warning exists but overflow_state is also set, clear overflow_state
          // Downgrade warning takes priority
          try {
            await supabase
              .from('profiles')
              .update({
                overflow_state: 'none',
                overflow_started_at: null,
              })
              .eq('id', userId);
            console.log('[OverflowBanner] Cleared overflow_state because downgrade_warning_started_at exists');
          } catch (error) {
            console.warn('Failed to clear overflow_state:', error);
            // Continue anyway
          }
        } else if (mightBeDowngrade && !hasDowngradeWarning) {
          // This might be a downgrade scenario - don't set overflow_state yet
          // Wait a bit to allow downgrade_warning_started_at to be set
          console.log('[OverflowBanner] Detected potential downgrade scenario - not setting overflow_state yet');
        }

        // Calculate days remaining
        let daysRemaining: number | null = null;
        let gracePeriodDays = 21; // Default for normal overflow
        let startedAt: string | null = null;

        // CRITICAL: PRIORITY - Check downgrade warning FIRST (has priority over normal overflow)
        // If downgrade_warning_started_at exists, ALWAYS use it, regardless of overflow_state or overflow_started_at
        if (hasDowngradeWarning && profile.downgrade_warning_started_at) {
          // Downgrade warning: 3 days warning period (user keeps all functions)
          // After 3 days: automatic suspension
          // After suspension: 14 days until deletion
          gracePeriodDays = 3;
          startedAt = profile.downgrade_warning_started_at;
          if (startedAt) {
            const startDate = new Date(startedAt);
            const daysElapsed = Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24));
            daysRemaining = Math.max(0, gracePeriodDays - daysElapsed);
          } else {
            daysRemaining = gracePeriodDays; // Full grace period remaining
          }
          console.log('[OverflowBanner] Using DOWNGRADE WARNING (3 days):', { startedAt, daysRemaining, gracePeriodDays });
        } else if (profile.overflow_started_at && !hasDowngradeWarning) {
          // Normal overflow: use grace_period_days from profile (default 21 days)
          // ONLY show this if NO downgrade warning is active
          gracePeriodDays = profile.overflow_grace_period_days || 21;
          startedAt = profile.overflow_started_at;
          if (startedAt) {
            const startDate = new Date(startedAt);
            const daysElapsed = Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24));
            daysRemaining = Math.max(0, gracePeriodDays - daysElapsed);
          } else {
            daysRemaining = gracePeriodDays; // Full grace period remaining
          }
          console.log('[OverflowBanner] Using NORMAL OVERFLOW (21 days):', { startedAt, daysRemaining, gracePeriodDays });
        } else {
          // Overflow exists but no start date set yet - use current time and default grace period
          // This can happen if the trigger hasn't fired yet
          // Only for normal overflow (not downgrade warning)
          if (!hasDowngradeWarning) {
            gracePeriodDays = profile.overflow_grace_period_days || 21;
            startedAt = new Date().toISOString();
            daysRemaining = gracePeriodDays; // Full grace period remaining
            console.log('[OverflowBanner] Using DEFAULT (no start date, 21 days):', { startedAt, daysRemaining, gracePeriodDays });
          } else {
            // This shouldn't happen, but if downgrade_warning_started_at exists but is null, use 3 days
            gracePeriodDays = 3;
            startedAt = new Date().toISOString();
            daysRemaining = gracePeriodDays;
            console.warn('[OverflowBanner] WARNING: hasDowngradeWarning=true but downgrade_warning_started_at is null!');
          }
        }

        // Use 'active' state if overflow exists, even if state is 'none'
        const effectiveState = state !== 'none' ? state : 'active';

        setOverflowStatus({
          state: effectiveState,
          startedAt,
          gracePeriodDays,
          daysRemaining,
          storageUsed,
          storageLimit,
          isDowngradeWarning: hasDowngradeWarning,
        });
      } catch (error) {
        console.error('Error in overflow check:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkOverflowState();

    // Subscribe to profile changes
    const channel = supabase
      .channel('overflow-status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        () => {
          checkOverflowState();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  if (isLoading || !overflowStatus || isDismissed) {
    return null;
  }

  // Calculate overflow amount safely
  const overflowAmount = Math.max(0, overflowStatus.storageUsed - overflowStatus.storageLimit);
  const isExpired = overflowStatus.daysRemaining !== null && overflowStatus.daysRemaining === 0;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-white border-b border-gray-200 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-base font-semibold text-gray-900">
                  ⚠️ Storage Limit Exceeded
                </h3>
                {overflowStatus.daysRemaining !== null && (
                  <div className="flex items-center gap-1 text-sm text-amber-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">
                      {isExpired 
                        ? 'Grace period expired' 
                        : `${overflowStatus.daysRemaining} ${overflowStatus.daysRemaining === 1 ? 'day' : 'days'} remaining`}
                    </span>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                You are using <strong>{formatBytes(overflowStatus.storageUsed)}</strong> of{' '}
                <strong>{formatBytes(overflowStatus.storageLimit)}</strong>
                {overflowAmount > 0 && (
                  <> ({' '}<strong className="text-red-600">+{formatBytes(overflowAmount)} exceeded</strong>{' '})</>
                )}
              </p>
              
              {overflowStatus.isDowngradeWarning ? (
                // Downgrade warning: User keeps all functions for 3 days
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-amber-800 font-medium mb-2">
                    ⚠️ <strong>Warning Period:</strong> You have {overflowStatus.daysRemaining} {overflowStatus.daysRemaining === 1 ? 'day' : 'days'} to delete files or upgrade your plan. During this period, all functions remain available.
                  </p>
                  <p className="text-xs text-amber-800 font-medium">
                    After {overflowStatus.gracePeriodDays} days, your account will be automatically suspended. After suspension, you will have 14 days to download your data before the account is permanently deleted.
                  </p>
                </div>
              ) : (
                // Normal overflow: Functions are restricted
                <>
                  <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
                    <span className="text-gray-600">Restricted:</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">No Uploads</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">No Versioning</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">No Sharing</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium flex items-center gap-1">
                      <Download className="w-3 h-3" />
                      Downloads available
                    </span>
                  </div>
                </>
              )}
            </div>
            
            <div className="text-sm text-gray-600 mb-3">
              {isExpired ? (
                <p className="text-red-600 font-medium">
                  ⚠️ Your data may be deleted at any time. Please back up your data now.
                </p>
              ) : (
                <p>
                  Delete files or upgrade your plan to remove the restrictions.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                onClick={() => {
                  setIsDismissed(true);
                  navigate(ROUTES.CLOUD_EXPLORER);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                <span>Delete Files</span>
              </button>
              <button
                onClick={() => {
                  setIsDismissed(true);
                  navigate(ROUTES.BILLING);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Upgrade Plan
              </button>
            </div>

            {/* Contact Information */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>{SUPPORT_EMAIL}</span>
              </a>
              <span className="text-gray-400">or</span>
              <button
                onClick={() => navigate(ROUTES.SUPPORT)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Create Support Ticket</span>
              </button>
              <span className="text-xs text-gray-500">- For questions, please contact us via email or create a support ticket</span>
            </div>
          </div>
          
          {/* Close button */}
          <button
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label="Close banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Export function to clear banner dismissal (used by Header button)
 */
export const clearOverflowBannerDismissal = () => {
  // Trigger a custom event to notify the banner component
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('overflow-banner-reopen'));
  }
};
